import { useState } from "react";
import { toast } from "sonner";
import SectionHeader from "../components/SectionHeader";
import SEO from "../components/SEO";
import { BRAND } from "../lib/content";
import { submitPartnershipInquiry, submitMeetingRequest, openCapabilitiesDeck } from "../lib/api";
import { Download, ArrowUpRight, Calendar, Clock } from "lucide-react";

const AREAS = ["Defense", "Aerospace / eVTOL", "Marine", "Energy", "Robotics & Actuators", "Hydraulics", "Investor"];

export default function ContactPage() {
  const [inq, setInq] = useState({
    company: "",
    name: "",
    email: "",
    role: "",
    interest_area: "Defense",
    briefing_requested: true,
    capabilities_deck_requested: true,
    message: "",
  });
  const [busy, setBusy] = useState(false);

  const [meet, setMeet] = useState({
    name: "",
    email: "",
    company: "",
    preferred_date: "",
    preferred_time: "10:00",
    timezone_label: "PT",
    topic: "Technical briefing",
  });
  const [meetBusy, setMeetBusy] = useState(false);

  async function onSubmitInquiry(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await submitPartnershipInquiry(inq);
      toast.success("Briefing request received. We'll reply within 48 hours.");
      setInq({ ...inq, message: "" });
    } catch (err) {
      toast.error("Submission failed. Please email us directly.");
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitMeeting(e) {
    e.preventDefault();
    setMeetBusy(true);
    try {
      await submitMeetingRequest(meet);
      toast.success("Meeting request submitted. A confirmation email will follow.");
      setMeet({ ...meet, topic: "Technical briefing" });
    } catch (err) {
      toast.error("Could not submit meeting request. Try again.");
    } finally {
      setMeetBusy(false);
    }
  }

  return (
    <main data-testid="page-contact">
      <SEO
        path="/contact"
        image="/brand/og-contact.jpg"
        title="Contact & Partnerships"
        description="Engage Logarithmic Technologies. Partnership inquiries, technical briefings under NDA, and meeting scheduling for defense, eVTOL, marine, energy and robotics teams."
      />
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 border-b border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <SectionHeader
              kicker="/ contact · partner engagement"
              title={<>Start a briefing.<br />Under NDA, if you prefer.</>}
              lead="Three ways in: a partnership inquiry, a scheduled technical meeting,
or the capabilities brief. Pick any or all."
            />
          </div>
          <div className="md:col-span-4 space-y-2 font-mono-tech text-[12px] uppercase tracking-widest text-white/70">
            <div>{BRAND.address}</div>
            <div><a className="u-link" href={`tel:${BRAND.phone}`}>{BRAND.phone}</a></div>
            <div><a className="u-link break-all" href={`mailto:${BRAND.contactEmail}`}>{BRAND.contactEmail}</a></div>
          </div>
        </div>
      </section>

      <section className="relative py-20 md:py-24" data-testid="contact-grid">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-0 border border-white/10">
          {/* INQUIRY */}
          <form
            onSubmit={onSubmitInquiry}
            className="p-8 md:p-12 border-b md:border-b-0 md:border-r border-white/10"
            data-testid="partnership-form"
          >
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
              01 / partnership inquiry
            </div>
            <h3 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tighter">
              Request a briefing
            </h3>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <Field label="Company" value={inq.company} onChange={(v) => setInq({ ...inq, company: v })} required id="partnership-company" />
              <Field label="Name" value={inq.name} onChange={(v) => setInq({ ...inq, name: v })} required id="partnership-name" />
              <Field label="Work email" type="email" value={inq.email} onChange={(v) => setInq({ ...inq, email: v })} required id="partnership-email" />
              <Field label="Role" value={inq.role} onChange={(v) => setInq({ ...inq, role: v })} id="partnership-role" />
            </div>

            <div className="mt-4">
              <label className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                Interest area
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {AREAS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => setInq({ ...inq, interest_area: a })}
                    data-testid={`interest-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={`font-mono-tech text-[11px] uppercase tracking-widest border px-3 py-2 transition ${
                      inq.interest_area === a
                        ? "border-white bg-white text-black"
                        : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 flex gap-6 items-center text-sm text-white/70">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={inq.briefing_requested}
                  onChange={(e) => setInq({ ...inq, briefing_requested: e.target.checked })}
                  data-testid="checkbox-briefing"
                  className="accent-[#FF3B30]"
                />
                <span className="font-mono-tech text-[11px] uppercase tracking-widest">
                  30-min briefing
                </span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={inq.capabilities_deck_requested}
                  onChange={(e) => setInq({ ...inq, capabilities_deck_requested: e.target.checked })}
                  data-testid="checkbox-deck"
                  className="accent-[#FF3B30]"
                />
                <span className="font-mono-tech text-[11px] uppercase tracking-widest">
                  Capabilities deck
                </span>
              </label>
            </div>

            <div className="mt-5">
              <label className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                Notes (program, timeline, platform)
              </label>
              <textarea
                rows={4}
                value={inq.message}
                onChange={(e) => setInq({ ...inq, message: e.target.value })}
                className="mt-2 w-full bg-transparent border border-white/15 focus:border-white px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none resize-none"
                placeholder="e.g. Tiltrotor gearbox redesign, 500 kW, 2026 prototype window."
                data-testid="partnership-message"
              />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={busy}
                data-testid="partnership-submit"
                className="inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-60 text-white font-mono-tech uppercase tracking-widest text-xs px-6 py-4"
              >
                {busy ? "Transmitting…" : "Submit inquiry"}
                <ArrowUpRight size={14} />
              </button>
              <button
                type="button"
                onClick={openCapabilitiesDeck}
                data-testid="contact-deck"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white px-5 py-4 font-mono-tech uppercase tracking-widest text-[11px]"
              >
                <Download size={14} /> Capabilities brief
              </button>
            </div>
          </form>

          {/* SCHEDULE */}
          <form
            id="schedule"
            onSubmit={onSubmitMeeting}
            className="p-8 md:p-12"
            data-testid="meeting-form"
          >
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
              02 / schedule a meeting
            </div>
            <h3 className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tighter">
              Book a technical slot
            </h3>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <Field label="Name" value={meet.name} onChange={(v) => setMeet({ ...meet, name: v })} required id="meeting-name" />
              <Field label="Work email" type="email" value={meet.email} onChange={(v) => setMeet({ ...meet, email: v })} required id="meeting-email" />
              <Field label="Company" value={meet.company} onChange={(v) => setMeet({ ...meet, company: v })} required id="meeting-company" />
              <Field label="Topic" value={meet.topic} onChange={(v) => setMeet({ ...meet, topic: v })} id="meeting-topic" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <Field
                label="Preferred date"
                icon={<Calendar size={12} />}
                type="date"
                value={meet.preferred_date}
                onChange={(v) => setMeet({ ...meet, preferred_date: v })}
                required
                id="meeting-date"
              />
              <Field
                label="Preferred time"
                icon={<Clock size={12} />}
                type="time"
                value={meet.preferred_time}
                onChange={(v) => setMeet({ ...meet, preferred_time: v })}
                id="meeting-time"
              />
            </div>

            <div className="mt-4">
              <label className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                Timezone
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {["PT", "MT", "CT", "ET", "GMT", "CET"].map((tz) => (
                  <button
                    type="button"
                    key={tz}
                    onClick={() => setMeet({ ...meet, timezone_label: tz })}
                    data-testid={`tz-${tz.toLowerCase()}`}
                    className={`font-mono-tech text-[11px] uppercase tracking-widest border px-3 py-2 ${
                      meet.timezone_label === tz
                        ? "border-white bg-white text-black"
                        : "border-white/15 text-white/70 hover:text-white hover:border-white/40"
                    }`}
                  >
                    {tz}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={meetBusy}
                data-testid="meeting-submit"
                className="inline-flex items-center gap-3 bg-white hover:bg-white/90 disabled:opacity-60 text-black font-mono-tech uppercase tracking-widest text-xs px-6 py-4"
              >
                {meetBusy ? "Reserving…" : "Request slot"}
                <ArrowUpRight size={14} />
              </button>
            </div>

            <div className="mt-10 border-t border-white/10 pt-6 font-mono-tech text-[10px] uppercase tracking-widest text-white/40 leading-relaxed">
              Slots are typically 30 minutes. After submitting, our team will confirm
              availability and send a calendar invite. For classified discussions,
              indicate clearance level in Topic.
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, icon, id }) {
  return (
    <div>
      <label htmlFor={id} className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-2">
        {icon}
        {label}
        {required && <span className="text-[#FF3B30]">*</span>}
      </label>
      <input
        id={id}
        data-testid={id}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent border border-white/15 focus:border-white px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none"
      />
    </div>
  );
}
